import { CardBody, Table, Tooltip, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { metricsAtom } from '@/atoms/metric';
import { renderedComparisonRadarDataAtom, renderedRadarDataAtom } from '@/atoms/radar';
import { TableOverlay } from '@/components/card/TableOverlay';
import { calculate360MetricKeys, getAsteriskAndTooltipFor360Metric } from '@/components/tables/AggsTable.utils';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { useDecimalPlaces } from '@/hooks/useDecimalPlaces';
import { useHasEmptyRadar } from '@/hooks/useHasEmptyRadar';
import { useStatus } from '@/hooks/useStatus';
import { MetricDefinition } from '@/types/metric';
import { StatusTranslationKey } from '@/types/status';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { BioDataItem } from './RadarBioTable';
import { RadarTableRow } from './RadarTableRow';

const NUM_ROWS_TO_DISPLAY_WHEN_TEMPLATE_DEFINITION_UNAVAILABLE = 9;
const SPOKE_ICON_COLUMN_WIDTH = '2rem';

export type RadarTableDisplayProps = { isInteractive?: boolean; showSpokes?: boolean };

export const RadarTableBase = ({
  section = 'player',
  isInteractive = true,
  isLoading = false,
  templates = [],
  radarPercentiles = {},
  radarTemplateId,
  unavailable360MetricKeys = [],
  metricData = [],
  minutesPlayed = 0,
  minutesPlayed360 = 0,
  comparisonPercentiles = {},
  comparisonMinutesPlayed = 0,
  comparisonMinutesPlayed360 = 0,
  entityName,
  bioData,
  comparisonEntityName,
  showSpokes = true,
}: RadarTableDisplayProps & {
  section?: 'player' | 'team';
  radarTemplateId?: string;
  isLoading?: boolean;
  templates?: UserConfigRadarTemplate[];
  radarPercentiles?: Record<string, number | undefined>;
  unavailable360MetricKeys?: string[];
  metricData?: MetricDefinition[];
  minutesPlayed?: number;
  minutesPlayed360?: number;
  comparisonPercentiles?: Record<string, number | undefined>;
  comparisonMinutesPlayed?: number;
  comparisonMinutesPlayed360?: number;
  entityName?: string;
  bioData?: BioDataItem[];
  comparisonEntityName?: string;
}) => {
  const { t } = useTranslation('radar');
  const renderedRadarData = useAtomValue(renderedRadarDataAtom);
  const renderedComparisonRadarData = useAtomValue(renderedComparisonRadarDataAtom);
  const hasComparisonData = renderedComparisonRadarData.length > 0;
  const setMetrics = useSetAtom(metricsAtom);
  const hasEmptyRadar = useHasEmptyRadar();
  const physicalMetricsEnabled = usePhysicalMetricsFeature();
  const formatNumberDecimals = useDecimalPlaces();
  const threeSixtyMetricKeys = calculate360MetricKeys(metricData);
  const loadingStatusProps = useStatus('general', StatusTranslationKey.LOADING);

  const selectedTemplate = templates.find(template => template.configId === radarTemplateId);

  useEffect(() => {
    if (!isLoading && selectedTemplate) {
      setMetrics(selectedTemplate.definition.metrics.map(metric => metric.key));
    }
  }, [radarTemplateId, isLoading, templates, setMetrics]);

  const placeholderRowCount =
    selectedTemplate?.definition.metrics.length ?? NUM_ROWS_TO_DISPLAY_WHEN_TEMPLATE_DEFINITION_UNAVAILABLE;
  const rowsToRender =
    renderedRadarData.length > 0
      ? renderedRadarData
      : // This data isn't rendered, we just need a number of rows in the array to create a table of the correct size
        Array.from({ length: placeholderRowCount }, (_, i) => ({
          key: `placeholder-${i}`,
          value: 0,
          decimalPlaces: 0,
          hasValue: false,
          source: undefined,
        }));

  const comparisonRadarDataByKey = useMemo(
    () => new Map(renderedComparisonRadarData.map(radarItem => [radarItem.key, radarItem])),
    [renderedComparisonRadarData],
  );

  // The metric-name column has no width so it absorbs the remaining space; value/percentile share
  // the rest proportionally and shrink when comparison columns are present.
  const valueColumnWidth = hasComparisonData ? '15%' : '20%';
  const labelColSpan = showSpokes ? 2 : 1;
  const columns: { id: string; width?: string }[] = [
    ...(showSpokes ? [{ id: 'spoke', width: SPOKE_ICON_COLUMN_WIDTH }] : []),
    { id: 'metric-name' },
    { id: 'value', width: valueColumnWidth },
    { id: 'percentile', width: valueColumnWidth },
    ...(hasComparisonData
      ? [
          { id: 'comparison-value', width: valueColumnWidth },
          { id: 'comparison-percentile', width: valueColumnWidth },
        ]
      : []),
  ];

  const metricsHeaderRow = (
    <Table.Row>
      {showSpokes && <Table.HeaderCell />}
      <Table.HeaderCell size="regular" />
      <Table.HeaderCell size="regular" textAlign="right">
        {t('table.value')}
      </Table.HeaderCell>
      <Table.HeaderCell size="regular" textAlign="right">
        <Tooltip placement="left-centre" content={<div className="max-w-[200px]">{t(`table.tooltip.${section}`)}</div>}>
          {t('table.percentile')}
        </Tooltip>
      </Table.HeaderCell>
      {hasComparisonData && (
        <>
          <Table.HeaderCell size="regular" textAlign="right" className="border-l border-canvas-primary-main">
            {t('table.value')}
          </Table.HeaderCell>
          <Table.HeaderCell size="regular" textAlign="right">
            {t('table.percentile')}
          </Table.HeaderCell>
        </>
      )}
    </Table.Row>
  );

  return (
    <div className={twJoin('relative overflow-x-auto', hasComparisonData && '[&_table]:min-w-[500px]')}>
      <Table>
        <colgroup>
          {columns.map(({ id, width }) => (
            <col key={id} style={width ? { width } : undefined} />
          ))}
        </colgroup>
        <Table.Head>
          {bioData ? (
            <Table.Row>
              <Table.HeaderCell colSpan={labelColSpan} />
              <Table.HeaderCell colSpan={2}>
                <div className="whitespace-normal">{entityName ?? ''}</div>
              </Table.HeaderCell>
              {hasComparisonData && (
                <Table.HeaderCell
                  colSpan={2}
                  className={twJoin(comparisonEntityName && 'border-l border-canvas-primary-main')}
                >
                  <div className="whitespace-normal">{comparisonEntityName ?? ''}</div>
                </Table.HeaderCell>
              )}
            </Table.Row>
          ) : (
            metricsHeaderRow
          )}
        </Table.Head>
        <Table.Body>
          {bioData?.map(item => (
            <Table.Row key={item.id ?? item.label}>
              <Table.DataCell colSpan={labelColSpan} textAlign="right">
                <Typography variant="headingSmall" as="span">
                  {item.label}
                </Typography>
              </Table.DataCell>
              <Table.DataCell colSpan={2} className="overflow-hidden">
                <Typography variant="bodyMedium" as="div">
                  <div className="whitespace-normal">{item.value}</div>
                </Typography>
              </Table.DataCell>
              {hasComparisonData && (
                <Table.DataCell
                  colSpan={2}
                  className={twJoin('overflow-hidden', comparisonEntityName && 'border-l border-canvas-primary-main')}
                >
                  <Typography variant="bodyMedium" as="div">
                    <div className="whitespace-normal">{comparisonEntityName ? item.comparisonValue ?? '' : ''}</div>
                  </Typography>
                </Table.DataCell>
              )}
            </Table.Row>
          ))}
          {bioData && metricsHeaderRow}
          {rowsToRender.map(({ key, value, decimalPlaces, hasValue, source }, index) => {
            const is360Unavailable = unavailable360MetricKeys.includes(key);
            const rowIsInteractive = isInteractive && !(physicalMetricsEnabled && source === 'physical');
            const displayPlaceholderValue = hasEmptyRadar && !is360Unavailable;
            const { showAsteriskWithTooltip, tooltipContent } = getAsteriskAndTooltipFor360Metric(
              t,
              key,
              threeSixtyMetricKeys,
              minutesPlayed,
              minutesPlayed360,
            );
            const { showAsteriskWithTooltip: comparisonShowAsterisk, tooltipContent: comparisonTooltipContent } =
              getAsteriskAndTooltipFor360Metric(
                t,
                key,
                threeSixtyMetricKeys,
                comparisonMinutesPlayed,
                comparisonMinutesPlayed360,
              );
            // True when the comparison player has no 360 data for a 360 metric,
            // regardless of whether the primary player has 360 data.
            const isComparison360Unavailable =
              hasComparisonData && threeSixtyMetricKeys.has(key) && comparisonMinutesPlayed360 === 0;

            return (
              <RadarTableRow
                key={key}
                metricKey={key}
                value={value}
                decimalPlaces={decimalPlaces}
                index={index}
                totalRows={rowsToRender.length}
                showMetricName={renderedRadarData.length > 0}
                isInteractive={rowIsInteractive}
                percentile={radarPercentiles[key]}
                hasValue={hasValue}
                is360Unavailable={is360Unavailable}
                displayPlaceholderValue={displayPlaceholderValue}
                showAsteriskWithTooltip={showAsteriskWithTooltip}
                tooltipContent={tooltipContent}
                hasComparisonData={hasComparisonData}
                comparisonPercentile={comparisonPercentiles[key]}
                comparisonValue={comparisonRadarDataByKey.get(key)?.value}
                comparisonHasValue={comparisonRadarDataByKey.get(key)?.hasValue}
                isComparison360Unavailable={isComparison360Unavailable}
                comparisonShowAsterisk={comparisonShowAsterisk}
                comparisonTooltipContent={comparisonTooltipContent}
                formatNumberDecimals={formatNumberDecimals}
                showSpokes={showSpokes}
              />
            );
          })}
        </Table.Body>
      </Table>
      {isLoading && (
        <TableOverlay>
          <CardBody {...loadingStatusProps} />
        </TableOverlay>
      )}
    </div>
  );
};
